import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Howl } from 'howler';
import { useTranslation } from 'react-i18next';

import { ChatContext } from './context';
import { useSignalrConnection, useVideo } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectPatients } from 'store/patientQueue';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { HubPatient } from 'types';
import { ConversationUnreadMessagesData } from './hooks';
import { ChatConversationRef, ChatMessagePayload, ChatTypingPayload, ChatUiMessage, ChatUpdatedRef } from 'types/chat';
import { parseIdentity } from 'utils';
import vibelongNote from 'assets/audio/chat/music_vibelong_note.mp3';

type ChatProviderProps = {
  children?: JSX.Element;
};

export function ChatProvider({ children }: ChatProviderProps) {
  const { t } = useTranslation('providerRoom');
  const {
    signalrConnection,
    signalrConnectionId,
    events,
    joinRoomChat,
    sendPrivateMessageByUniqueName,
    sendRoomMessage,
    startTypingPrivate,
    endTypingPrivate,
    startTypingRoom,
    endTypingRoom,
  } = useSignalrConnection();
  const user = useAppSelector(selectUser);
  const patients: HubPatient[] = useAppSelector(selectPatients);
  const waitingRoomProfile = useAppSelector(selectWaitingRoomProfile);
  const { room } = useVideo();

  const prevPatients: MutableRefObject<HubPatient[]> = useRef<HubPatient[]>([]);
  const typingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const notificationSoundRef = useRef<Howl | null>(null);
  const lastNotificationPlayedAtRef = useRef<number>(0);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRoomChatOpen, setIsRoomChatOpen] = useState(false);
  const [openedConversationUniqueName, setOpenedConversationUniqueName] = useState<string | null>(null);
  const [openedConversationTargetId, setOpenedConversationTargetId] = useState<string | null>(null);
  const [openedRoomName, setOpenedRoomName] = useState<string | null>(null);

  const [privateMessagesByConversation, setPrivateMessagesByConversation] = useState<Map<string, ChatUiMessage[]>>(
    new Map(),
  );
  const [roomMessagesByName, setRoomMessagesByName] = useState<Map<string, ChatUiMessage[]>>(new Map());

  const [privateParticipantsByConversation, setPrivateParticipantsByConversation] = useState<
    Map<string, Map<string, string>>
  >(new Map());
  const [roomParticipantsByName, setRoomParticipantsByName] = useState<Map<string, Map<string, string>>>(new Map());

  const [privateTypingByConversation, setPrivateTypingByConversation] = useState<Map<string, string[]>>(new Map());
  const [roomTypingByName, setRoomTypingByName] = useState<Map<string, string[]>>(new Map());

  const [conversationsUnreadMessagesData, setConversationsUnreadMessagesData] = useState<
    Map<string, ConversationUnreadMessagesData>
  >(new Map());
  const [lastUpdatedChat, setLastUpdatedChat] = useState<ChatUpdatedRef | null>(null);

  const isChatOpenRef = useRef(isChatOpen);
  const isRoomChatOpenRef = useRef(isRoomChatOpen);
  const openedConversationUniqueNameRef = useRef<string | null>(openedConversationUniqueName);
  const openedRoomNameRef = useRef<string | null>(openedRoomName);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  useEffect(() => {
    isRoomChatOpenRef.current = isRoomChatOpen;
  }, [isRoomChatOpen]);

  useEffect(() => {
    openedConversationUniqueNameRef.current = openedConversationUniqueName;
  }, [openedConversationUniqueName]);

  useEffect(() => {
    openedRoomNameRef.current = openedRoomName;
  }, [openedRoomName]);

  useEffect(() => {
    notificationSoundRef.current = new Howl({
      src: [vibelongNote],
      preload: true,
      volume: 1,
    });

    return () => {
      notificationSoundRef.current?.unload();
      notificationSoundRef.current = null;
    };
  }, []);

  const identity = user?.clinicUser.id ?? signalrConnectionId ?? '';
  const youLabel = t('chatYou');
  const isConnected = !!signalrConnection.current && !!signalrConnectionId;

  const getHostDisplayName = useCallback((): string => {
    const fullName = `${waitingRoomProfile?.firstName ?? ''} ${waitingRoomProfile?.lastName ?? ''}`.trim();
    return waitingRoomProfile?.preferedDisplayName || fullName || t('hostFallback');
  }, [t, waitingRoomProfile?.firstName, waitingRoomProfile?.lastName, waitingRoomProfile?.preferedDisplayName]);

  const getRoomParticipantNameFromVideoRoom = useCallback(
    (connectionId: string): string | null => {
      const localIdentity = room?.localParticipant?.identity;
      if (localIdentity) {
        const [localConnectionId, localDisplayName] = parseIdentity(localIdentity);
        if (localConnectionId === connectionId && localDisplayName) {
          return localDisplayName;
        }
      }

      for (const participant of room?.participants.values() ?? []) {
        const [participantConnectionId, participantDisplayName] = parseIdentity(participant.identity);
        if (participantConnectionId === connectionId && participantDisplayName) {
          return participantDisplayName;
        }
      }

      return null;
    },
    [room?.localParticipant?.identity, room?.participants],
  );

  const getDisplayNameForRoomParticipant = useCallback(
    (fromConnectionId: string, authorName?: string): string => {
      if (fromConnectionId === signalrConnectionId || authorName === identity) {
        return youLabel;
      }

      const fromRoomIdentity = getRoomParticipantNameFromVideoRoom(fromConnectionId);
      if (fromRoomIdentity) {
        return fromRoomIdentity;
      }

      const hostProviderId = waitingRoomProfile?.providerId;
      if (!user && hostProviderId && authorName === hostProviderId) {
        return getHostDisplayName();
      }

      if (user) {
        const patientName = patients.find((p) => p.connectionId === fromConnectionId)?.userName;
        if (patientName) {
          return patientName;
        }
      }

      if (authorName && authorName.trim().length > 0 && authorName !== hostProviderId) {
        return authorName;
      }

      return fromConnectionId;
    },
    [
      getHostDisplayName,
      getRoomParticipantNameFromVideoRoom,
      identity,
      patients,
      signalrConnectionId,
      youLabel,
      user,
      waitingRoomProfile?.providerId,
    ],
  );

  const resolvePrivateConversationIds = useCallback(
    (uniqueName: string): { providerId: string; patientId: string } | null => {
      if (!uniqueName.startsWith('$chat-')) return null;

      const providerIdentity = user?.clinicUser.id;
      if (providerIdentity) {
        const providerPrefix = `$chat-${providerIdentity}-`;
        if (uniqueName.toLowerCase().startsWith(providerPrefix.toLowerCase())) {
          const patientId = uniqueName.substring(providerPrefix.length);
          return patientId ? { providerId: providerIdentity, patientId } : null;
        }
      }

      if (identity) {
        const patientSuffix = `-${identity}`;
        if (uniqueName.endsWith(patientSuffix)) {
          const providerId = uniqueName.substring('$chat-'.length, uniqueName.length - patientSuffix.length);
          return providerId ? { providerId, patientId: identity } : null;
        }
      }

      return null;
    },
    [identity, user?.clinicUser.id],
  );

  const getDisplayNameForPrivateParticipant = useCallback(
    (participantKey: string, ids: { providerId: string; patientId: string }): string => {
      if (participantKey === ids.providerId) {
        if (user) return youLabel;
        return getHostDisplayName();
      }

      if (participantKey === ids.patientId) {
        if (!user) return youLabel;
        return patients.find((p) => p.connectionId === ids.patientId)?.userName || ids.patientId;
      }

      return participantKey;
    },
    [getHostDisplayName, patients, user, youLabel],
  );

  const updateUnread = useCallback((key: string, unreadMessagesCount: number) => {
    setConversationsUnreadMessagesData((prev) => {
      const next = new Map(prev);
      next.set(key, { conversationUniqueName: key, unreadMessagesCount });
      return next;
    });
  }, []);

  const incrementUnread = useCallback((key: string) => {
    setConversationsUnreadMessagesData((prev) => {
      const next = new Map(prev);
      const current = next.get(key)?.unreadMessagesCount ?? 0;
      next.set(key, { conversationUniqueName: key, unreadMessagesCount: current + 1 });
      return next;
    });
  }, []);

  const playNotificationSound = useCallback(() => {
    const sound = notificationSoundRef.current;
    if (!sound) return;

    // Prevent rapid duplicate SignalR events from retriggering and clipping the tone.
    const now = Date.now();
    if (now - lastNotificationPlayedAtRef.current < 900) return;
    lastNotificationPlayedAtRef.current = now;

    if (sound.playing()) return;

    sound.seek(0);
    sound.play();
  }, []);

  const appendPrivateMessage = useCallback(
    (payload: ChatMessagePayload) => {
      const uniqueName = payload.uniqueName ?? '';
      if (!uniqueName || !payload.message) return;

      const ids = resolvePrivateConversationIds(uniqueName);
      const isMyMessageByConnection = !!signalrConnectionId && payload.fromConnectionId === signalrConnectionId;
      const author = isMyMessageByConnection
        ? identity
        : ids && payload.fromConnectionId === ids.patientId
        ? ids.patientId
        : ids
        ? ids.providerId
        : payload.fromConnectionId;
      const message: ChatUiMessage = {
        author,
        body: payload.message,
        dateCreated: payload.sentAtUtc ? new Date(payload.sentAtUtc) : new Date(),
        type: 'text',
      };

      setPrivateMessagesByConversation((prev) => {
        const next = new Map(prev);
        next.set(uniqueName, [...(next.get(uniqueName) ?? []), message]);
        return next;
      });

      setPrivateParticipantsByConversation((prev) => {
        const next = new Map(prev);
        const participantsMap = new Map(next.get(uniqueName) ?? []);
        if (ids) {
          const existingProviderName = participantsMap.get(ids.providerId);
          const existingPatientName = participantsMap.get(ids.patientId);
          participantsMap.set(
            ids.providerId,
            existingProviderName || getDisplayNameForPrivateParticipant(ids.providerId, ids),
          );
          participantsMap.set(
            ids.patientId,
            existingPatientName || getDisplayNameForPrivateParticipant(ids.patientId, ids),
          );
        } else {
          const existingAuthorName = participantsMap.get(author);
          participantsMap.set(
            author,
            existingAuthorName || (payload.fromConnectionId === signalrConnectionId ? youLabel : payload.authorName),
          );
        }
        participantsMap.set(identity, youLabel);
        next.set(uniqueName, participantsMap);
        return next;
      });

      const chatIsOpen = isChatOpenRef.current && openedConversationUniqueNameRef.current === uniqueName;
      if (chatIsOpen) {
        updateUnread(uniqueName, 0);
      } else {
        incrementUnread(uniqueName);
      }
      setLastUpdatedChat({ uniqueName });
    },
    [
      getDisplayNameForPrivateParticipant,
      identity,
      incrementUnread,
      resolvePrivateConversationIds,
      signalrConnectionId,
      updateUnread,
      youLabel,
    ],
  );

  const appendRoomMessage = useCallback(
    (payload: ChatMessagePayload) => {
      const roomName = payload.roomName ?? '';
      if (!roomName || !payload.message) return;

      const isMyMessage =
        (Boolean(signalrConnectionId) && payload.fromConnectionId === signalrConnectionId) ||
        payload.authorName === identity;
      const author = isMyMessage ? identity : payload.fromConnectionId;
      const message: ChatUiMessage = {
        author,
        body: payload.message,
        dateCreated: payload.sentAtUtc ? new Date(payload.sentAtUtc) : new Date(),
        type: 'text',
      };

      setRoomMessagesByName((prev) => {
        const next = new Map(prev);
        next.set(roomName, [...(next.get(roomName) ?? []), message]);
        return next;
      });

      setRoomParticipantsByName((prev) => {
        const next = new Map(prev);
        const participantsMap = new Map(next.get(roomName) ?? []);
        participantsMap.set(
          author,
          isMyMessage ? youLabel : getDisplayNameForRoomParticipant(payload.fromConnectionId, payload.authorName),
        );
        participantsMap.set(identity, youLabel);
        next.set(roomName, participantsMap);
        return next;
      });

      const roomIsOpen = isRoomChatOpenRef.current && openedRoomNameRef.current === roomName;
      if (roomIsOpen) {
        updateUnread(roomName, 0);
      } else {
        incrementUnread(roomName);
      }
      setLastUpdatedChat({ uniqueName: roomName });
    },
    [getDisplayNameForRoomParticipant, identity, incrementUnread, signalrConnectionId, updateUnread, youLabel],
  );

  const setTypingState = useCallback((isGroup: boolean, key: string, participantId: string, isTyping: boolean) => {
    if (!key || !participantId) return;

    const updateState = (prev: Map<string, string[]>) => {
      const next = new Map(prev);
      const current = next.get(key) ?? [];
      const withoutParticipant = current.filter((value) => value !== participantId);
      next.set(key, isTyping ? [...withoutParticipant, participantId] : withoutParticipant);
      return next;
    };

    if (isGroup) {
      setRoomTypingByName(updateState);
    } else {
      setPrivateTypingByConversation(updateState);
    }
  }, []);

  useEffect(() => {
    const onPrivateMessage = (message: ChatMessagePayload) => {
      appendPrivateMessage(message);

      if (message.uniqueName && message.uniqueName === openedConversationUniqueNameRef.current) {
        if (message.fromConnectionId !== signalrConnectionId) {
          setOpenedConversationTargetId(message.fromConnectionId);
        } else if (message.toConnectionId) {
          setOpenedConversationTargetId(message.toConnectionId);
        }
      }

      if (message.fromConnectionId !== signalrConnectionId && !isChatOpenRef.current && message.uniqueName) {
        playNotificationSound();

        setOpenedConversationUniqueName(message.uniqueName);
        setOpenedConversationTargetId(message.fromConnectionId);
        setIsChatOpen(true);
      }
    };

    const onRoomMessage = (message: ChatMessagePayload) => {
      appendRoomMessage(message);

      if (
        message.fromConnectionId !== signalrConnectionId &&
        message.roomName &&
        message.roomName === room?.name &&
        !isRoomChatOpenRef.current
      ) {
        playNotificationSound();
      }
    };

    const onPrivateTypingStarted = (message: ChatTypingPayload) => {
      const uniqueName = message.uniqueName ?? '';
      const ids = resolvePrivateConversationIds(uniqueName);
      const participantId = ids
        ? message.fromConnectionId === ids.patientId
          ? ids.patientId
          : ids.providerId
        : message.fromConnectionId === signalrConnectionId
        ? identity
        : message.fromConnectionId;

      if (ids) {
        setPrivateParticipantsByConversation((prev) => {
          const next = new Map(prev);
          const participantsMap = new Map(next.get(uniqueName) ?? []);
          const existingProviderName = participantsMap.get(ids.providerId);
          const existingPatientName = participantsMap.get(ids.patientId);
          participantsMap.set(
            ids.providerId,
            existingProviderName || getDisplayNameForPrivateParticipant(ids.providerId, ids),
          );
          participantsMap.set(
            ids.patientId,
            existingPatientName || getDisplayNameForPrivateParticipant(ids.patientId, ids),
          );
          next.set(uniqueName, participantsMap);
          return next;
        });
      }
      setTypingState(false, uniqueName, participantId, true);
    };

    const onPrivateTypingEnded = (message: ChatTypingPayload) => {
      const uniqueName = message.uniqueName ?? '';
      const ids = resolvePrivateConversationIds(uniqueName);
      const participantId = ids
        ? message.fromConnectionId === ids.patientId
          ? ids.patientId
          : ids.providerId
        : message.fromConnectionId === signalrConnectionId
        ? identity
        : message.fromConnectionId;
      setTypingState(false, uniqueName, participantId, false);
    };

    const onRoomTypingStarted = (message: ChatTypingPayload) => {
      const roomName = message.roomName ?? '';
      const participantId = message.fromConnectionId === signalrConnectionId ? identity : message.fromConnectionId;

      setRoomParticipantsByName((prev) => {
        const next = new Map(prev);
        const participantsMap = new Map(next.get(roomName) ?? []);
        participantsMap.set(
          participantId,
          getDisplayNameForRoomParticipant(message.fromConnectionId, message.authorName),
        );
        participantsMap.set(identity, youLabel);
        next.set(roomName, participantsMap);
        return next;
      });

      setTypingState(true, roomName, participantId, true);
    };

    const onRoomTypingEnded = (message: ChatTypingPayload) => {
      const roomName = message.roomName ?? '';
      const participantId = message.fromConnectionId === signalrConnectionId ? identity : message.fromConnectionId;
      setTypingState(true, roomName, participantId, false);
    };

    events.on('receivePrivateMessage', onPrivateMessage);
    events.on('receiveRoomMessage', onRoomMessage);
    events.on('privateTypingStarted', onPrivateTypingStarted);
    events.on('privateTypingEnded', onPrivateTypingEnded);
    events.on('roomTypingStarted', onRoomTypingStarted);
    events.on('roomTypingEnded', onRoomTypingEnded);

    return () => {
      events.off('receivePrivateMessage', onPrivateMessage);
      events.off('receiveRoomMessage', onRoomMessage);
      events.off('privateTypingStarted', onPrivateTypingStarted);
      events.off('privateTypingEnded', onPrivateTypingEnded);
      events.off('roomTypingStarted', onRoomTypingStarted);
      events.off('roomTypingEnded', onRoomTypingEnded);
    };
  }, [
    appendPrivateMessage,
    appendRoomMessage,
    events,
    getDisplayNameForPrivateParticipant,
    getDisplayNameForRoomParticipant,
    identity,
    resolvePrivateConversationIds,
    room?.name,
    setTypingState,
    signalrConnectionId,
    playNotificationSound,
    youLabel,
  ]);

  useEffect(() => {
    if (!openedConversationUniqueName) return;
    updateUnread(openedConversationUniqueName, 0);
  }, [openedConversationUniqueName, updateUnread]);

  useEffect(() => {
    if (!openedRoomName) return;
    updateUnread(openedRoomName, 0);
  }, [openedRoomName, updateUnread]);

  useEffect(() => {
    if (isRoomChatOpen && !openedRoomName && room?.name) {
      setOpenedRoomName(room.name);
    }
  }, [isRoomChatOpen, openedRoomName, room?.name]);

  useEffect(() => {
    if (openedRoomName) {
      joinRoomChat(openedRoomName).catch(() => undefined);
    }
  }, [joinRoomChat, openedRoomName]);

  // Join room chat group as soon as the call room is known so participants receive
  // messages/typing/unread updates even before opening the Group Chat panel.
  useEffect(() => {
    if (!isConnected || !room?.name) return;
    joinRoomChat(room.name).catch(() => undefined);
  }, [isConnected, joinRoomChat, room?.name]);

  const triggerPrivateTyping = useCallback(
    (targetConnectionId: string) => {
      if (!targetConnectionId) return;
      startTypingPrivate(targetConnectionId).catch(() => undefined);

      const timerKey = `private:${targetConnectionId}`;
      const existing = typingTimersRef.current.get(timerKey);
      if (existing) clearTimeout(existing);

      const timeout = setTimeout(() => {
        endTypingPrivate(targetConnectionId).catch(() => undefined);
      }, 1500);

      typingTimersRef.current.set(timerKey, timeout);
    },
    [endTypingPrivate, startTypingPrivate],
  );

  const triggerRoomTyping = useCallback(
    (roomName: string) => {
      if (!roomName) return;
      startTypingRoom(roomName).catch(() => undefined);

      const timerKey = `room:${roomName}`;
      const existing = typingTimersRef.current.get(timerKey);
      if (existing) clearTimeout(existing);

      const timeout = setTimeout(() => {
        endTypingRoom(roomName).catch(() => undefined);
      }, 1500);

      typingTimersRef.current.set(timerKey, timeout);
    },
    [endTypingRoom, startTypingRoom],
  );

  const conversation: ChatConversationRef | null = useMemo(() => {
    if (!openedConversationUniqueName) return null;
    return {
      uniqueName: openedConversationUniqueName,
      typing: () => {
        if (openedConversationTargetId) {
          triggerPrivateTyping(openedConversationTargetId);
        }
      },
    };
  }, [openedConversationTargetId, openedConversationUniqueName, triggerPrivateTyping]);

  const roomConversation: ChatConversationRef | null = useMemo(() => {
    if (!openedRoomName) return null;
    return {
      uniqueName: openedRoomName,
      typing: () => triggerRoomTyping(openedRoomName),
    };
  }, [openedRoomName, triggerRoomTyping]);

  const allConversations: ChatConversationRef[] = useMemo(() => {
    const privateRefs = Array.from(privateMessagesByConversation.keys()).map((uniqueName) => ({
      uniqueName,
      typing: () => undefined,
    }));
    const roomRefs = Array.from(roomMessagesByName.keys()).map((uniqueName) => ({
      uniqueName,
      typing: () => undefined,
    }));
    return [...privateRefs, ...roomRefs];
  }, [privateMessagesByConversation, roomMessagesByName]);

  const openChat = useCallback(
    (providerId: string, patientId: string, targetDisplayName?: string) => {
      const uniqueName = `$chat-${providerId}-${patientId}`;
      setOpenedConversationUniqueName(uniqueName);

      const targetConnectionId = identity === providerId ? patientId : providerId;
      setOpenedConversationTargetId(targetConnectionId);
      setIsChatOpen(true);
      updateUnread(uniqueName, 0);

      setPrivateParticipantsByConversation((prev) => {
        const next = new Map(prev);
        const participantsMap = new Map(next.get(uniqueName) ?? []);
        const ids = { providerId, patientId };
        const defaultProviderName = getDisplayNameForPrivateParticipant(providerId, ids);
        const defaultPatientName = getDisplayNameForPrivateParticipant(patientId, ids);
        participantsMap.set(providerId, identity === providerId && targetDisplayName ? youLabel : defaultProviderName);
        participantsMap.set(
          patientId,
          identity === providerId && targetDisplayName ? targetDisplayName : defaultPatientName,
        );
        next.set(uniqueName, participantsMap);
        return next;
      });
    },
    [getDisplayNameForPrivateParticipant, identity, updateUnread, youLabel],
  );

  const openChatByUniqueName = useCallback(
    (uniqueName: string) => {
      if (!uniqueName) return;
      setOpenedConversationUniqueName(uniqueName);
      setIsChatOpen(true);
      updateUnread(uniqueName, 0);

      const parsedIds = resolvePrivateConversationIds(uniqueName);
      if (parsedIds) {
        const targetConnectionId = identity === parsedIds.providerId ? parsedIds.patientId : parsedIds.providerId;
        setOpenedConversationTargetId(targetConnectionId);

        setPrivateParticipantsByConversation((prev) => {
          const next = new Map(prev);
          const participantsMap = new Map(next.get(uniqueName) ?? []);
          participantsMap.set(
            parsedIds.providerId,
            getDisplayNameForPrivateParticipant(parsedIds.providerId, parsedIds),
          );
          participantsMap.set(parsedIds.patientId, getDisplayNameForPrivateParticipant(parsedIds.patientId, parsedIds));
          next.set(uniqueName, participantsMap);
          return next;
        });
      } else {
        setOpenedConversationTargetId(null);
      }
    },
    [getDisplayNameForPrivateParticipant, identity, resolvePrivateConversationIds, updateUnread],
  );

  const openRoomChat = useCallback(
    (roomName: string) => {
      if (!roomName) return;
      setIsRoomChatOpen(true);
      setOpenedRoomName(roomName);
      updateUnread(roomName, 0);
      joinRoomChat(roomName).catch(() => undefined);
    },
    [joinRoomChat, updateUnread],
  );

  const addPatientToRoomChat = useCallback(
    async (_patientId: string, roomName: string) => {
      if (!roomName) return;
      await joinRoomChat(roomName);
    },
    [joinRoomChat],
  );

  const closeChat = (): void => {
    setOpenedConversationUniqueName(null);
    setOpenedConversationTargetId(null);
    setIsChatOpen(false);
  };

  const closeRoomChat = (): void => {
    setIsRoomChatOpen(false);
    setOpenedRoomName(null);
  };

  const sendMessageInternal = useCallback(
    async (text: string) => {
      if (!isConnected) {
        throw new Error('Chat connection not ready');
      }

      if (!openedConversationUniqueName) {
        throw new Error('No chat target defined for this private conversation');
      }

      await sendPrivateMessageByUniqueName({
        uniqueName: openedConversationUniqueName,
        message: text,
        clientMessageId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
    },
    [isConnected, openedConversationUniqueName, sendPrivateMessageByUniqueName],
  );

  const sendMessage = useCallback(
    async (text: string): Promise<number> => {
      await sendMessageInternal(text);
      return Date.now();
    },
    [sendMessageInternal],
  );

  const sendMessageToRoomChat = useCallback(
    async (message: string | FormData): Promise<number> => {
      if (message instanceof FormData) {
        throw new Error('File messages are not supported in the SignalR chat refactor yet');
      }

      const roomName = openedRoomName ?? room?.name;
      if (!roomName) {
        throw new Error('Room not available');
      }

      await joinRoomChat(roomName);
      await sendRoomMessage({
        roomName,
        message,
        clientMessageId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });

      return Date.now();
    },
    [joinRoomChat, openedRoomName, room?.name, sendRoomMessage],
  );

  // Close provider's open 1:1 chat when the patient leaves queue.
  useEffect(() => {
    if (prevPatients.current !== patients && user) {
      const removedPatientsChatsNames: string[] = [];

      prevPatients.current.forEach((prevPatient: HubPatient) => {
        if (
          !patients.map((currentPatient: HubPatient) => currentPatient.connectionId).includes(prevPatient.connectionId)
        ) {
          removedPatientsChatsNames.push(`$chat-${user.clinicUser.id}-${prevPatient.connectionId}`);
        }
      });

      if (openedConversationUniqueName && removedPatientsChatsNames.includes(openedConversationUniqueName)) {
        setOpenedConversationUniqueName(null);
        setOpenedConversationTargetId(null);
        setIsChatOpen(false);
      }
    }

    prevPatients.current = patients;
  }, [openedConversationUniqueName, patients, user]);

  const getMessages = useCallback(
    (isGroup: boolean): ChatUiMessage[] => {
      if (isGroup) {
        return openedRoomName ? roomMessagesByName.get(openedRoomName) ?? [] : [];
      }
      return openedConversationUniqueName ? privateMessagesByConversation.get(openedConversationUniqueName) ?? [] : [];
    },
    [openedConversationUniqueName, openedRoomName, privateMessagesByConversation, roomMessagesByName],
  );

  const getParticipants = useCallback(
    (isGroup: boolean): Map<string, string> => {
      if (isGroup) {
        return new Map(openedRoomName ? roomParticipantsByName.get(openedRoomName) ?? [] : []);
      }
      return new Map(
        openedConversationUniqueName ? privateParticipantsByConversation.get(openedConversationUniqueName) ?? [] : [],
      );
    },
    [openedConversationUniqueName, openedRoomName, privateParticipantsByConversation, roomParticipantsByName],
  );

  const getTypingParticipants = useCallback(
    (isGroup: boolean): string[] => {
      if (isGroup) {
        return openedRoomName ? roomTypingByName.get(openedRoomName) ?? [] : [];
      }
      return openedConversationUniqueName ? privateTypingByConversation.get(openedConversationUniqueName) ?? [] : [];
    },
    [openedConversationUniqueName, openedRoomName, privateTypingByConversation, roomTypingByName],
  );

  return (
    <ChatContext.Provider
      value={{
        conversation,
        roomConversation,
        isConnected,
        identity,
        allConversations,
        conversationsUnreadMessagesData,
        lastUpdatedChat,
        openChat,
        openChatByUniqueName,
        openRoomChat,
        addPatientToRoomChat,
        closeChat,
        closeRoomChat,
        sendMessage,
        sendMessageToRoomChat,
        isChatOpen,
        getMessages,
        getParticipants,
        getTypingParticipants,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
